import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Globals } from 'src/app/globals';
import { ContextService } from 'src/app/core/service/api/context.service';
import { ContextIqAnalysisRequest as contextAPIPayload, PhysicalContextAnalysisRequest } from './iq-context-analysis';
import { contextMapPolygonStyles, convertContextPolygonCoordinates, extractAllStakeholders } from 'src/app/core/util/contextPolygonStyle';
import { forkJoin } from 'rxjs';
import { PaginationParams } from 'src/app/core/service/common/pagination-params';
import { getColorCode } from 'src/app/core/util/color';
import { ContextSidebarPanelComponent } from './context-sidebar-panel/context-sidebar-panel.component';
import { isEqual } from 'src/assets/js/underscore-min';
import { area } from '@turf/turf';

@Component({
  selector: 'app-iq-context-analysis',
  templateUrl: './iq-context-analysis.component.html',
  styleUrl: './iq-context-analysis.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IqContextAnalysisComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainerRef!: ElementRef<HTMLElement>;
  @ViewChild(ContextSidebarPanelComponent) contextRightSidebar!: ContextSidebarPanelComponent; 
  map!: mapboxgl.Map;
  draw!: MapboxDraw;
  showNotification = signal(false);
  toggleRightSidePanel = signal(false);
  toggleContextDetailPanel = signal(false);
  isProcessing = signal(false);
  loadingSavedAnalysis = signal(false);
  showPolygonLimitExceededErrorPopup = signal(false);
  showScrollToTop = signal(false);
  sidebarCollapsed = signal(false);
  allStakeholdersLocations: any = [];
  contextAnalysisResponse:any = [];
  physicalContextAnalysisResponse:any = [];
  selectedContextItem: any = null;
  selectedAreaGeometry: any = null;
  contextTabType: string = '';
  totalItems: number = 0;
  paginator=new PaginationParams();
  private lastDrawMode: string | null = null;
  private isChangingMode = false;
  private hasSavedAnalysis = false; // Track if we loaded a saved analysis from backend
  private mapPopup: mapboxgl.Popup | null = null;
  private multiItemPopups: mapboxgl.Popup[] = [];
  private tooltipHideTimeout: any = null;
  private arePhysicalItemsVisible = false;
  private readonly MAX_AREA_M2 = 2_000_000;
  private readonly UNIFIED_POLYGONS_SOURCE = 'unified-context-polygons-source';
  private readonly UNIFIED_POINTS_SOURCE = 'unified-context-points-source';
  private readonly UNIFIED_CONTEXT_OUTLINE = 'unified-context-polygons-outline';
  private readonly UNIFIED_CONTEXT_POLYGON_FILL = 'unified-context-polygons-fill';
  private readonly UNIFIED_CONTEXT_POINTS_LAYER = 'unified-context-points-layer';
  private lastHoveredFeature: any = null;
  private eventListenersAttached = false;
  private mapResizeObserver: ResizeObserver | null = null;

  constructor(
    private globals: Globals, 
    private _contextService: ContextService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ){}

  ngOnInit(): void {
    this.paginator.orderBy('id');
    this.paginator.with(["locations,types,stakeholders,issues"]);
  }

  ngAfterViewInit(): void {
    (mapboxgl as any).accessToken = this.globals.mapBoxToken;
    const mapEl = this.mapContainerRef.nativeElement;
    this.map = new mapboxgl.Map({
      container: mapEl,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [0, 0],
      zoom: 2,
      projection: 'mercator',
      attributionControl: false
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.mapResizeObserver = new ResizeObserver(() => {
        if (this.map) {
          this.map.resize();
        }
      });
      this.mapResizeObserver.observe(mapEl);
    }

    this.map.addControl(new mapboxgl.NavigationControl(), 'top-left');

    const compassBtn = this.map.getContainer().querySelector('.mapboxgl-ctrl-compass');
    if (compassBtn) {
      compassBtn.addEventListener('click', (e: Event) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (this.toggleContextDetailPanel()) {
          this.toggleContextDetailPanel.set(false);
          this.selectedContextItem = null;
          this.restoreAllContextLayersOpacity();
          this.hideTooltip();
        }
        this.focusOnAnalysisArea();
      }, true);
    }

    this.map.on('load', async () => {
      await this.loadImage('assets/png/new_regulatory.png', 'regulatory-icon'),
      await this.loadImage('assets/png/new_physical.png', 'physical-icon'),
      await this.loadImage('assets/png/regulatory_num.png', 'regulatory_num'),
      await this.loadImage('assets/png/physical_num.png', 'physical_num')

      const NoDragSimpleSelect = {
        ...MapboxDraw.modes.simple_select,
        dragMove: function() {},
        onMouseDown: function(state: any, e: any) { e.originalEvent.stopPropagation(); },
        onMouseMove: function(state: any, e: any) { e.originalEvent.stopPropagation(); },
        onMouseUp:   function(state: any, e: any) { e.originalEvent.stopPropagation(); },
        onMouseOver: function() { this.map.getCanvas().style.cursor = ''; },
        onMouseOut:  function() { this.map.getCanvas().style.cursor = ''; }
      };
      const NoDragDirectSelect = {
        ...MapboxDraw.modes.direct_select,
        dragFeature: function() {},
        onMouseOver: function() { this.map.getCanvas().style.cursor = ''; },
        onMouseOut:  function() { this.map.getCanvas().style.cursor = ''; }
      };
      // Add controls
      this.draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        styles: contextMapPolygonStyles(),
        modes: {
          ...MapboxDraw.modes,
          simple_select: NoDragSimpleSelect,
          direct_select: NoDragDirectSelect
        }
      });
      this.map.addControl(this.draw, 'bottom-left');
      this.setDrawButtonsVisibility(false);

      this.map.addSource('drawn-points-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      this.map.addSource(this.UNIFIED_POLYGONS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      this.map.addSource(this.UNIFIED_POINTS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      this.attachDelegatedEventListeners();

      // Draw polygon
      this.map.on('draw.create', (e:any) => {
        if(e.features[0].geometry.type == 'Polygon'){
          if (!this.validatePolygonArea(e.features[0])) {
            return; // stop further processing
          }
          this.showPolygonLimitExceededErrorPopup.set(false);
          this.selectedAreaGeometry = e.features[0].geometry;
          this.showNotification.set(true);
          if (!this.loadingSavedAnalysis()) {
            this.hasSavedAnalysis = false;
          }
          // Disable polygon drawing when polygon is created
          this.updateDrawControls();
        }
        this.handleDrawCreate(e);
      });

      // Delete drawn polygon
      this.map.on('draw.delete', (e: any) => {
        this.handleDrawDelete(e);
        this.showNotification.set(false);
        this.selectedAreaGeometry = null;
        
        // if (this.hasSavedAnalysis) {
          this.deleteSavedAnalysis();
        // }
        
        this.updateDrawControls();
      });

      // Update drawn polygon
      this.map.on('draw.update', (e: any) => {
        const feature = e.features[0];
        if (feature.geometry.type === 'Polygon') {
          if (!this.validatePolygonArea(feature)) {
            return;
          }
          this.showPolygonLimitExceededErrorPopup.set(false);
        }
        this.handleDrawUpdate(e);
      });

      this.loadSavedAnalysisPolygon();
      this.scheduleMapResize();
    });
  }

  /** Remeasure Mapbox after layout (e.g. shownRoute dashboard→app) — avoids 0×0 canvas. */
  private scheduleMapResize(): void {
    if (!this.map) {
      return;
    }
    requestAnimationFrame(() => {
      this.map.resize();
      setTimeout(() => this.map.resize(), 0);
    });
  }

  ngOnDestroy(): void {
    if (this.mapResizeObserver) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
    if (this.map) {
      if (this.draw) {
        this.map.removeControl(this.draw);
        this.draw = null as any;
      }
      this.map.remove();
      this.map = null as any;
    }
  }

  private handleDrawCreate(e: any): void {
    this.updateDrawnPointsSource();
  }

  private handleDrawDelete(e: any): void {
    this.updateDrawnPointsSource();
    this.showPolygonLimitExceededErrorPopup.set(false);
    // Close the context sidebar panel
    this.toggleRightSidePanel.set(false);
    
    // Close the detail panel if open
    this.toggleContextDetailPanel.set(false);
    this.selectedContextItem = null;
    
    this.hideTooltipImmediately();
    this.clearAllAnalysisLayers();
    
    this.hideStakeholdersLayer(true);
    const stakeholdersSource = this.map.getSource('stakeholders-source');
    if (stakeholdersSource) {
      const source = stakeholdersSource as mapboxgl.GeoJSONSource;
      source.setData({ type: 'FeatureCollection', features: [] });
    }
    
    // Reset analysis data
    this.contextAnalysisResponse = [];
    this.physicalContextAnalysisResponse = [];
    this.allStakeholdersLocations = [];
    
    // Trigger change detection
    this.cdr.detectChanges();
  }

  private handleDrawUpdate(e: any): void {
    this.updateDrawnPointsSource();
  }

  private updateDrawnPointsSource(): void {
    const source = this.map.getSource('drawn-points-source') as mapboxgl.GeoJSONSource;

    // Do nothing if the source doesn’t exist yet
    if (!source) return;

    const data = this.draw.getAll();
    const points = data.features.filter(
      (feature: any) => feature.geometry.type === 'Point'
    );

    source.setData({
      type: 'FeatureCollection',
      features: points
    });
  }

  // Image loader
  private loadImage(url: string, name: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.map.loadImage(url, (error, image) => {
        if (error || !image) {
          console.error('Failed to load icon:', url);
          reject(error);
        } else {
          this.map.addImage(name, image);
          resolve();
        }
      });
    });
  }

  private validatePolygonArea(feature: any): boolean {
    const polygonArea = area(feature); // m²

    if (polygonArea > this.MAX_AREA_M2) {
      this.showPolygonLimitExceededErrorPopup.set(true);
      // Disable analysis button / notification
      this.showNotification.set(false);

      // Keep polygon selected so user sees what’s wrong
      this.selectedAreaGeometry = feature.geometry;

      this.updateDrawControls();

      return false;
    }

    this.showPolygonLimitExceededErrorPopup.set(false);
    return true;
  }

  onNewAnalysis() {
    this.showPolygonLimitExceededErrorPopup.set(false);
    this.isProcessing.set(true);
    this.updateTrashButtonState();
    const payload = this.preparePayload(this.selectedAreaGeometry);
    this.getContextAiAnalysis( payload.payload, { polygon: payload.polygon });
  }

  reDrawPolygon(): void {
    this.showPolygonLimitExceededErrorPopup.set(false);

    this.draw.deleteAll();

    this.selectedAreaGeometry = null;
    this.showNotification.set(false);

    this.disablePolygonButton(false);

    setTimeout(() => {
      (this.draw as any).changeMode('draw_polygon');
    }, 0);
  }

  preparePayload(geometry: any) {
    const polygon: any = convertContextPolygonCoordinates(geometry?.coordinates[0]);
    const payload: contextAPIPayload = {
      polygon,
    }
    return {payload, polygon};
  }

  getContextAiAnalysis(ctxPayload: contextAPIPayload, physicalCtxPayload : PhysicalContextAnalysisRequest) {
    forkJoin({
      ctx: this._contextService.getContextAiAnalysis(ctxPayload),
      physicals: this._contextService.getPhysicalContextAiAnalysis(physicalCtxPayload),
    }).subscribe(({ ctx, physicals, }) => {
      this.contextAnalysisResponse = ctx?.metadata?.response?.contexts ?? [];
      this.physicalContextAnalysisResponse = physicals?.metadata?.response ?? [];
      const savedData = [];

      this.contextAnalysisResponse.forEach(suggestedItem => {
        suggestedItem.context_items = suggestedItem.context_items ?? [];

        const ctxType = suggestedItem.context_type?.toLowerCase();
        if (!ctxType) return;

        const matchingSaved = this.filterSavedByType(savedData, ctxType);

        matchingSaved.forEach(savedContext => {
          suggestedItem.context_items.push({
            ...savedContext,
            isManual: true
          });
        });
      });

      const savedPhysical = this.filterSavedByType(savedData, 'physical');

      savedPhysical.forEach(savedContext => {
        this.physicalContextAnalysisResponse.push({
          ...savedContext,
          isManual: true
        });
      });

      this.allStakeholdersLocations = extractAllStakeholders(ctx?.metadata?.response || { contexts: [] });
      const physicalStks = this.physicalContextAnalysisResponse.flatMap(item => item.stakeholders || []);
      this.allStakeholdersLocations= [...this.allStakeholdersLocations, ...physicalStks];
      this.countTotalItems();

      setTimeout(() => {
        this.toggleRightSidePanel.set(true);
        this.showNotification.set(false);
        this.isProcessing.set(false);
        this.displayContextByType('physical');
        this.loadingSavedAnalysis.set(false);
        this.updateTrashButtonState();
      });
    }, (error) => {
      this.isProcessing.set(false);
      this.showNotification.set(false);
      this.loadingSavedAnalysis.set(false);
      this.updateTrashButtonState();
    });
  }

  countTotalItems() {
    let total = 0;
    this.contextAnalysisResponse.forEach((subType) => {
      subType.context_subtypes?.forEach((subSubtype: any) => {
        total += subSubtype?.context_items.length || 0;
      })
    });

    this.totalItems = this.physicalContextAnalysisResponse.length + total;
  }

  private renderStakeholdersOnMap(): void {
    const sourceId = 'stakeholders-source';
    const layerId = 'stakeholders-layer';

    const geojson: any = this.getStakeholdersGeoJSON();

    if (this.map.getSource(sourceId)) {
      const source = this.map.getSource(sourceId) as mapboxgl.GeoJSONSource;
      source.setData(geojson);
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', 'visible');
      }
      return;
    }

    this.map.addSource(sourceId, {
      type: 'geojson',
      data: geojson
    });

    this.map.addLayer({
      id: layerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': 8,
        'circle-color': '#4FC3F7',      // sky blue
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-opacity': 0.9
      }
    });

  }

  private attachDelegatedEventListeners(): void {
    if (this.eventListenersAttached) return;
    this.eventListenersAttached = true;

    this.ngZone.runOutsideAngular(() => {
      this.map.on('click', (e: any) => {
        const possibleLayers = [];
        if (this.map.getLayer('stakeholders-layer')) {
          possibleLayers.push('stakeholders-layer');
        }
        if (this.map.getLayer(this.UNIFIED_CONTEXT_POLYGON_FILL)) {
          possibleLayers.push(this.UNIFIED_CONTEXT_POLYGON_FILL, this.UNIFIED_CONTEXT_OUTLINE);
        }
        if (this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER)) {
          possibleLayers.push(this.UNIFIED_CONTEXT_POINTS_LAYER);
        }

        if (possibleLayers.length === 0) return;

        const features = this.map.queryRenderedFeatures(e.point, {
          layers: possibleLayers
        });

        if (features && features.length > 0) {
          const feature = features[0];
          
          if (feature.layer.id === 'stakeholders-layer') {
            const stakeholderData = feature.properties?.stakeholderData 
              ? JSON.parse(feature.properties.stakeholderData) 
              : null;
            if (stakeholderData) {
              this.ngZone.run(() => this.onStakeholderPinClick(stakeholderData));
            }
            return;
          }

          const data = feature.properties?.itemData ? JSON.parse(feature.properties.itemData) : null;
          const type = feature.properties?.contextType || 'context';
          if (data) {
            this.ngZone.run(() => {
              if (type !== 'stakeholder') {
                this.onContextPinClick(data, type);
              }
            });
          }
        }
      });

      let hoverThrottle: any = null;
      this.map.on('mousemove', (e: any) => {
        if (hoverThrottle) return;
        
        hoverThrottle = setTimeout(() => {
          hoverThrottle = null;
          
          const possibleLayers = [];
          if (this.map.getLayer('stakeholders-layer')) {
            possibleLayers.push('stakeholders-layer');
          }
          if (this.map.getLayer(this.UNIFIED_CONTEXT_POLYGON_FILL)) {
            possibleLayers.push(this.UNIFIED_CONTEXT_POLYGON_FILL, this.UNIFIED_CONTEXT_OUTLINE);
          }
          if (this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER)) {
            possibleLayers.push(this.UNIFIED_CONTEXT_POINTS_LAYER);
          }

          if (possibleLayers.length === 0) {
            this.map.getCanvas().style.cursor = '';
            this.lastHoveredFeature = null;
            this.hideTooltip();
            return;
          }

          const features = this.map.queryRenderedFeatures(e.point, {
            layers: possibleLayers
          });

          if (features && features.length > 0) {
            const feature = features[0];
            this.map.getCanvas().style.cursor = 'pointer';
            
            const featureId = feature.id || feature.properties?.itemId || feature.properties?.itemData;
            if (this.lastHoveredFeature === featureId) return;
            this.lastHoveredFeature = featureId;

            if (feature.layer.id === 'stakeholders-layer') {
              const name = feature.properties?.name || 'Unknown Stakeholder';
              const address = feature.properties?.address || '';
              const geom = feature.geometry as any;
              const coordinates = geom.type === 'Point' && Array.isArray(geom.coordinates)
                ? geom.coordinates.slice()
                : e.lngLat;
              this.showTooltip(coordinates, name, address);
              return;
            }

            const data = feature.properties?.itemData ? JSON.parse(feature.properties.itemData) : null;
            if (data) {
              const title = data.text || data.name || 'Context Item';
              const address = data.address || '';
              const geom = feature.geometry as any;
              const coords = geom.type === 'Point' && Array.isArray(geom.coordinates)
                ? geom.coordinates.slice()
                : e.lngLat;
              this.showTooltip(coords, title, address);
            }
          } else {
            this.map.getCanvas().style.cursor = '';
            this.lastHoveredFeature = null;
            this.hideTooltip();
          }
        }, 50);
      });

      this.map.on('mouseleave', () => {
        this.map.getCanvas().style.cursor = '';
        this.lastHoveredFeature = null;
        this.hideTooltip();
      });
    });
  }

  private hideStakeholdersLayer(hide: boolean): void {
    const layerId = 'stakeholders-layer';
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', hide ? 'none' : 'visible');
    }
  }

  private hidePhysicalContextLayers(hide: boolean): void {
    const pointsLayer = this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER);
    if (pointsLayer) {
      if (hide) {
        this.map.setFilter(this.UNIFIED_CONTEXT_POINTS_LAYER, [
          '!=',
          ['get', 'contextType'],
          'physical'
        ]);
      } else {
        this.map.setFilter(this.UNIFIED_CONTEXT_POINTS_LAYER, null);
      }
    }

    const style = this.map.getStyle();
    if (!style) return;

    style.layers?.forEach(layer => {
      if (layer.type === 'symbol') {
        let shouldHide = false;
        
        try {
          const iconImage = this.map.getLayoutProperty(layer.id, 'icon-image');
          if (iconImage === 'physical-icon' || iconImage === 'physical_num') {
            shouldHide = true;
          }
        } catch (e) {
        }
        
        if (shouldHide) {
          this.map.setLayoutProperty(layer.id, 'visibility', hide ? 'none' : 'visible');
        }
      }
    });
  }

  private focusOnAnalysisArea(): void {
    if (this.toggleContextDetailPanel()) {
      return;
    }
    
    if (this.selectedAreaGeometry && this.selectedAreaGeometry.type === 'Polygon') {
      const bounds = new mapboxgl.LngLatBounds();
      const coords = this.selectedAreaGeometry.coordinates as any;
      coords.forEach((polygonRing: any) => {
        const rings = Array.isArray(polygonRing[0][0]) ? polygonRing : [polygonRing];
        rings.forEach((ring: any) => {
          ring.forEach((coord: number[]) => {
            bounds.extend([coord[0], coord[1]] as [number, number]);
          });
        });
      });
      
      if (!bounds.isEmpty()) {
        const padding = {
          top: 80,
          bottom: 80,
          left: 80,
          right: window.innerWidth * 0.5 + 80
        };
        this.map.fitBounds(bounds, { padding, maxZoom: 16 });
      }
    }
  }

  private getStakeholdersGeoJSON() {
    return {
      type: 'FeatureCollection',
      features: this.allStakeholdersLocations.map((st: any) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: st.locations?.points?.coordinates?.[0] ?? null
        },
        properties: {
          name: st.name,
          address: st.address,
          stakeholderData: JSON.stringify(st)
        }
      })).filter(f => f.geometry.coordinates)
    };
  }

  private filterSavedByType(savedData: any[], type: string) {
    return savedData.filter(item =>
      Array.isArray(item.types) &&
      item.types.some(t => t.name?.toLowerCase() === type)
    );
  }

  onItemSelection(selection: {item: any, type: string, isSingleItem?: boolean}){
    this.hideTooltip();
    if ((selection?.isSingleItem && !selection?.item && !selection?.type) || (Array.isArray(selection?.item?.context_items) && selection?.item?.context_items.length)) {
      this.toggleContextDetailPanel.set(false);
      this.selectedContextItem = null;
      this.focusOnAnalysisArea();
      this.clearAllContextLayers();
      return;
    }
    this.arePhysicalItemsVisible = this.selectedContextItem && this.selectedContextItem.type === 'Physical';
    this.selectedContextItem = selection;
    this.toggleContextDetailPanel.set(true);
    this.highlightContextItemOnMap(selection.item, selection.type, true, selection?.isSingleItem);
  }

  private highlightSingleItemOnMap(item: any, contextType: string): void {
    if (!item) return;

    // Match item by UUID, text, name, or amenity
    const itemId = item.id || item.uuid || null;
    const itemText = item.text || item.name || '';
    const itemAmenity = item.amenity || '';

    const matchConditions: any[] = ['any'];
    
    if (itemId) {
      matchConditions.push(['==', ['get', 'itemId'], itemId]);
    }
    if (itemText) {
      matchConditions.push(['==', ['get', 'name'], itemText]);
    }
    if (itemAmenity) {
      matchConditions.push(['==', ['get', 'amenity'], itemAmenity]);
    }

    if (matchConditions.length === 1) {
      this.highlightItemByDataMatch(item);
      return;
    }

    const color = getColorCode();

    if (this.map.getLayer(this.UNIFIED_CONTEXT_POLYGON_FILL)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POLYGON_FILL, 'fill-opacity', [
        'case',
        matchConditions,
        0.8,
        0.25
      ]);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POLYGON_FILL, 'fill-color', [
        'case',
        matchConditions,
        color,
        ['get', 'fillColor']
      ]);
    }

    if (this.map.getLayer(this.UNIFIED_CONTEXT_OUTLINE)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-opacity', [
        'case',
        matchConditions,
        1,
        1
      ]);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-width', [
        'case',
        matchConditions,
        4,
        2
      ]);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-color', [
        'case',
        matchConditions,
        '#ff8800',
        ['get', 'fillColor']
      ]);
    }

    // Highlight unified points layer
    if (this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'icon-opacity', [
        'case',
        matchConditions,
        1,
        1
      ]);
      this.map.setLayoutProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'icon-size', [
        'case',
        matchConditions,
        0.8,
        0.45
      ]);
    }
  }

  private highlightItemByDataMatch(item: any): void {
    const style = this.map.getStyle();
    if (!style) return;

    const itemId = item.id ? `all-${item.id}` : null;
    const foundLayers: string[] = [];

    style.layers?.forEach(layer => {
      if (itemId && layer.id.includes(itemId)) {
        foundLayers.push(layer.id);
      }
      
      if (!layer.source || typeof layer.source !== 'string' || layer.source.trim() === '') {
        return;
      }
      
      try {
        const source = this.map.getSource(layer.source);
        if (source && (source as any).data) {
          const data = (source as any).data;
          const features = data.features || (data.type === 'Feature' ? [data] : []);
          
          for (const feature of features) {
            if (feature.properties) {
              const itemData = feature.properties.itemData;
              if (itemData) {
                try {
                  const parsed = typeof itemData === 'string' ? JSON.parse(itemData) : itemData;
                  const matches = (parsed.text && item.text && parsed.text === item.text) ||
                                 (parsed.name && item.name && parsed.name === item.name) ||
                                 (parsed.id && item.id && parsed.id === item.id) ||
                                 (parsed.amenity && item.amenity && parsed.amenity === item.amenity);
                  
                  if (matches && !foundLayers.includes(layer.id)) {
                    foundLayers.push(layer.id);
                    break;
                  }
                } catch (e) {
                  // Skip if parsing fails
                }
              }
            }
          }
        }
      } catch (e) {
        // Skip if error
      }
    });

    const color = getColorCode();
    foundLayers.forEach(layerId => {
      const layer = this.map.getLayer(layerId);
      if (!layer) return;

      if (layer.type === 'fill') {
        this.map.setPaintProperty(layerId, 'fill-opacity', 0.8);
        this.map.setPaintProperty(layerId, 'fill-color', color);
      } else if (layer.type === 'line') {
        this.map.setPaintProperty(layerId, 'line-opacity', 1);
        this.map.setPaintProperty(layerId, 'line-width', 4);
        this.map.setPaintProperty(layerId, 'line-color', '#ff8800');
      } else if (layer.type === 'symbol') {
        this.map.setPaintProperty(layerId, 'icon-opacity', 1);
        this.map.setLayoutProperty(layerId, 'icon-size', 0.8);
      }
    });
  }


  private removeItemHighlight(): void {
    // Restore unified layers to normal appearance
    if (this.map.getLayer(this.UNIFIED_CONTEXT_POLYGON_FILL)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POLYGON_FILL, 'fill-opacity', 0.25);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POLYGON_FILL, 'fill-color', ['get', 'fillColor']);
    }

    if (this.map.getLayer(this.UNIFIED_CONTEXT_OUTLINE)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-opacity', 1);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-width', 2);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-color', ['get', 'fillColor']);
    }

    if (this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'icon-opacity', 1);
      this.map.setLayoutProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'icon-size', 0.45);
    }

    // Also restore old per-item layers for backward compatibility
    const style = this.map.getStyle();
    if (!style) return;

    style.layers?.forEach(layer => {
      if (this.isPhysicalContextLayer(layer)) {
        if (layer.type === 'fill') {
          this.map.setPaintProperty(layer.id, 'fill-opacity', 0.25);
        } else if (layer.type === 'line') {
          this.map.setPaintProperty(layer.id, 'line-opacity', 1);
          this.map.setPaintProperty(layer.id, 'line-width', 2);
        } else if (layer.type === 'symbol') {
          this.map.setPaintProperty(layer.id, 'icon-opacity', 1);
          this.map.setLayoutProperty(layer.id, 'icon-size', 0.45);
        }
      }
    });
  }

  onContextTypeChange(contextType: string) {
    if(this.isTabChanges(contextType)) {
      this.hideTooltip();
      this.toggleContextDetailPanel.set(false);
      this.selectedContextItem = null;
      this.focusOnAnalysisArea();
    };
    if (contextType === 'Stakeholders') {
      this.contextTabType = contextType;
      this.clearAllContextLayers();
      this.hidePhysicalContextLayers(true);
      this.hideStakeholdersLayer(false);
      this.renderStakeholdersOnMap();
    } else {
      this.hideStakeholdersLayer(true);
      if (contextType === 'Physical') {
        this.hidePhysicalContextLayers(false);
        if (this.isTabChanges(contextType)) {
          this.displayContextByType('physical');
        }
      }
      this.contextTabType = contextType;
    }
  }

  private isTabChanges(newContextType: string): boolean {
    const currentIsStakeholders = this.contextTabType === 'Stakeholders';
    const newIsStakeholders = newContextType === 'Stakeholders';
    return currentIsStakeholders !== newIsStakeholders;
  }

  onDetailClose(){
    this.selectedContextItem = null;
    this.toggleContextDetailPanel.set(false);
    this.restoreAllContextLayersOpacity();
    this.hideTooltip();
    if(this.contextTabType!=='Stakeholders') {
      this.hidePhysicalContextLayers(false);
      this.displayContextByType('physical');
    }
    if(this.contextTabType=='Stakeholders') {
      this.clearAllContextLayers();
      this.hideStakeholdersLayer(false);
      this.focusOnAnalysisArea();
    };
  }

  onRightPanelClose() {
    this.sidebarCollapsed.set(false);
    this.toggleRightSidePanel.set(false);
    // this.clearAllContextLayers();
  }

  onHoverContext(type: string, item: any) {
    if (!item) return;

    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }

    this.showContextItemTooltip(item, type);    
    this.highlightSingleItemOnMap(item, type);
  }

  onBlurContext() {
    this.hideTooltip();
    this.removeItemHighlight();
  }

  private showContextItemTooltip(item: any, contextType: string): void {
    if (!item) return;

    if (Array.isArray(item.context_items) && item.context_items.length > 0) {
      this.hideTooltipImmediately();

      item.context_items.forEach((ctxItem: any, index: number) => {
        const title = ctxItem.text || ctxItem.name || ctxItem.neighborhood_name || 'Context Item';

        const coords = this.extractTooltipCoordinates(ctxItem);
        if (!coords) return;

        const content = `
          <div>
            <div style="font-weight: 600; color: #333;">${title}</div>
          </div>
        `;

        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 15,
          anchor: 'bottom',
          className: 'map-tooltip multi-item-tooltip'
        })
          .setLngLat(coords)
          .setHTML(content)
          .addTo(this.map);

        if (!this.multiItemPopups) {
          this.multiItemPopups = [];
        }
        this.multiItemPopups.push(popup);
      });

      return;
    }

    this.showSingleContextTooltip(item);
  }

  private hideAllMultiItemTooltips(): void {
    if (this.multiItemPopups) {
      this.multiItemPopups.forEach(popup => popup.remove());
      this.multiItemPopups = [];
    }
  }

  private extractTooltipCoordinates(item: any): [number, number] | null {
    const pointCoords = item.locations?.points?.coordinates?.[0];
    if (pointCoords) return pointCoords;

    if (item.geometry) {
      return this.getPolygonCentroid(item.geometry);
    }

    if (item.locations?.polygons) {
      return this.getPolygonCentroid(item.locations.polygons);
    }

    return null;
  }

  private showSingleContextTooltip(item: any): void {
    const title = item.text || item.name || item.neighborhood_name || 'Context Item';
    let tooltipCoordinates: [number, number] | null = null;

    const contextPointCoords = item.locations?.points?.coordinates;
    if (Array.isArray(contextPointCoords) && contextPointCoords.length > 0) {
      tooltipCoordinates = contextPointCoords[0];
    }

    if (!tooltipCoordinates && item.geometry) {
      if (item.geometry.type === 'Polygon' || item.geometry.type === 'MultiPolygon') {
        tooltipCoordinates = this.getPolygonCentroid(item.geometry);
      }
    }

    if (!tooltipCoordinates && item.locations?.polygons) {
      if (item.locations.polygons.type === 'Polygon' || item.locations.polygons.type === 'MultiPolygon') {
        tooltipCoordinates = this.getPolygonCentroid(item.locations.polygons);
      }
    }

    if (!tooltipCoordinates && Array.isArray(item.stakeholders) && item.stakeholders.length > 0) {
      const firstStakeholder = item.stakeholders[0];
      const stakeholderCoords = firstStakeholder?.locations?.points?.coordinates;
      if (Array.isArray(stakeholderCoords) && stakeholderCoords.length > 0) {
        tooltipCoordinates = stakeholderCoords[0];
      }
    }

    if (tooltipCoordinates) {
      this.showTooltip(tooltipCoordinates, title);
    }
  }

  private getPolygonCentroid(geometry: any): [number, number] | null {
    try {
      const coords = geometry.coordinates;
      let allCoords: number[][] = [];

      const traverse = (coordArray: any[]) => {
        coordArray.forEach((c: any) => {
          if (typeof c[0] === 'number' && typeof c[1] === 'number') {
            allCoords.push(c);
          } else if (Array.isArray(c)) {
            traverse(c);
          }
        });
      };

      traverse(coords);

      if (allCoords.length > 0) {
        const avgLng = allCoords.reduce((sum, c) => sum + c[0], 0) / allCoords.length;
        const avgLat = allCoords.reduce((sum, c) => sum + c[1], 0) / allCoords.length;
        return [avgLng, avgLat];
      }
    } catch (e) {
      console.error('Error calculating polygon centroid:', e);
    }

    return null;
  }

  onSidebarScroll(event: Event): void {
    const element = event.target as HTMLElement;
    this.showScrollToTop.set(element.scrollTop > 300);
  }

  scrollToTop(): void {
    const sidebar = document.querySelector('.sidebar-panel-wrapper') as HTMLElement;
    if (sidebar) {
      sidebar.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  private displayContextByType(contextType: string): void {
    const polygonsSource = this.map.getSource(this.UNIFIED_POLYGONS_SOURCE) as mapboxgl.GeoJSONSource;
    const pointsSource = this.map.getSource(this.UNIFIED_POINTS_SOURCE) as mapboxgl.GeoJSONSource;
    
    if (polygonsSource) {
      polygonsSource.setData({ type: 'FeatureCollection', features: [] });
    }
    if (pointsSource) {
      pointsSource.setData({ type: 'FeatureCollection', features: [] });
    }

    // Show unified layers
    if (this.map.getLayer(this.UNIFIED_CONTEXT_POLYGON_FILL)) {
      this.map.setLayoutProperty(this.UNIFIED_CONTEXT_POLYGON_FILL, 'visibility', 'visible');
    }
    if (this.map.getLayer(this.UNIFIED_CONTEXT_OUTLINE)) {
      this.map.setLayoutProperty(this.UNIFIED_CONTEXT_OUTLINE, 'visibility', 'visible');
    }
    if (this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER)) {
      this.map.setLayoutProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'visibility', 'visible');
    }

    this.clearAllContextLayers();

    const bounds = new mapboxgl.LngLatBounds();
    let hasPoints = false;

    if (contextType === 'physical') {
      if (Array.isArray(this.physicalContextAnalysisResponse)) {
        this.physicalContextAnalysisResponse.forEach((item: any, index: number) => {
          const itemId = item.id || `all-physical-${item.uuid}`;
          const result = this.addContextToMap(item, itemId, 'physical', bounds);
          if (result) {
            this.arePhysicalItemsVisible = true;
            hasPoints = true;
          };
        });
      }
    } else {
      this.contextAnalysisResponse.forEach((context: any, ctxIndex: number) => {
        const currentContextType = this.normalizeContextType(context.context_type || '');
        const normalizedContextType = this.normalizeContextType(contextType);
        
        if (currentContextType !== normalizedContextType) return;
        
        if (Array.isArray(context.context_subtypes)) {
          context.context_subtypes.forEach((subtype: any) => {
            if (Array.isArray(subtype.context_items)) {
              subtype.context_items.forEach((item: any, itemIndex: number) => {
                const itemId = item.id || `all-context-${ctxIndex}-${itemIndex}`;
                const result = this.addContextToMap(item, itemId, normalizedContextType, bounds);
                if (result) hasPoints = true;
              });
            }
          });
        }

        if (Array.isArray(context.context_items)) {
          context.context_items.forEach((item: any, itemIndex: number) => {
            const itemId = item.id || `all-context-direct-${ctxIndex}-${itemIndex}`;
            const result = this.addContextToMap(item, itemId, normalizedContextType, bounds);
            if (result) hasPoints = true;
          });
        }
      });
    }

    if (!this.toggleContextDetailPanel()) {
      if (this.selectedAreaGeometry && this.selectedAreaGeometry.type === 'Polygon') {
        this.focusOnAnalysisArea();
      } else if (!bounds.isEmpty() && hasPoints) {
        const padding = {
          top: 80,
          bottom: 80,
          left: 80,
          right: window.innerWidth * 0.5 + 80
        };
        this.map.fitBounds(bounds, { padding, maxZoom: 16 });
      }
    }
  }

  private highlightContextItemOnMap(contextData: any, contextType: string, fitBound = true, isSingleItem?: boolean): void {
    const bounds = new mapboxgl.LngLatBounds();
    let hasPoints = false;
    if (contextType === 'Physical') {
      if(!this.arePhysicalItemsVisible) this.displayContextByType('physical');
      const coords = contextData?.locations?.points?.coordinates;
      if (Array.isArray(coords) && coords.length > 0) {
          bounds.extend(coords[0]);
          hasPoints = true;
      }
      this.showContextItemTooltip(contextData, contextType);
    } else if (isSingleItem && contextType !== 'Physical') {
      this.clearAllContextLayers();

      const normalizedContextType = this.normalizeContextType(contextType);
      const itemId = `all-context-${contextData.uuid}`;
      const result = this.addContextToMap(contextData, itemId, normalizedContextType, bounds);
      if (result) hasPoints = true;
      this.showContextItemTooltip(contextData, contextType);
    } else if (contextType === 'Stakeholder') {
      if (contextData?.id && contextData?.created_at) {
        const pi = this.physicalContextAnalysisResponse.filter(pi=> pi.uuid === contextData.contextUuid)
        contextData.locations = pi?.[0]?.locations;
      }
      const coords = contextData?.locations?.points?.coordinates;
      if (Array.isArray(coords) && coords.length > 0) {
        bounds.extend(coords[0]);
        hasPoints = true;

        this.showContextItemTooltip(contextData, contextType);
      }
    }

    if (!bounds.isEmpty() && hasPoints && fitBound) {
      this.zoomToItems(bounds, contextType);
    }
  }

  private zoomToItems(bounds: any, contextType: string) {
    const padding = {
      top: 80,
      bottom: 80,
      left: 80,
      right: window.innerWidth * 0.5 + 80
    };
    this.map.fitBounds(bounds, { padding, maxZoom: ['Physical','Stakeholder'].includes(contextType) ? 36 : 16, duration: 1000 });
  }

  private addContextToMap(item: any, itemId: string, contextType: string, bounds: mapboxgl.LngLatBounds): boolean {
    let hasData = false;
    const prefix = 'all-';

    let polygonGeometry = null;
    
    if (item.geometry && (item.geometry.type === 'Polygon' || item.geometry.type === 'MultiPolygon')) {
      polygonGeometry = item.geometry;
    }
    else if (item.locations?.polygons && 
             (item.locations.polygons.type === 'Polygon' || item.locations.polygons.type === 'MultiPolygon')) {
      polygonGeometry = item.locations.polygons;
    }
    
    if (polygonGeometry) {
      const color = item.color || item.polygon_color || undefined;
      this.addPolygonLayer(polygonGeometry, `${prefix}${itemId}`, contextType, false, color, item);
      this.extendBounds(bounds, polygonGeometry);
      hasData = true;
    }

    const contextPointCoords = item.locations?.points?.coordinates;
    const locType = item.locations?.locatable_type;

    if (Array.isArray(contextPointCoords) && contextPointCoords.length > 0) {
      const icon = locType === 'contexts' ? 'physical-icon' : 'regulatory-icon';
      this.addPointFeatures(contextPointCoords, `${prefix}${itemId}-points`, icon, false, item, contextType);
      contextPointCoords.forEach((c: any) => bounds.extend(c));
      hasData = true;
    }

    if (Array.isArray(item.stakeholders)) {
      const stakeholderCoords: number[][] = [];
      const stakeholderDataArray: any[] = [];

      item.stakeholders.forEach(st => {
        const coords = st?.locations?.points?.coordinates;
        if (Array.isArray(coords)) {
          coords.forEach(c => {
            stakeholderCoords.push(c);
            stakeholderDataArray.push(st);
          });
        }
      });

      if (stakeholderCoords.length > 0) {
        this.addPointFeatures(stakeholderCoords, `${prefix}${itemId}-stakeholders`, 'physical-icon', false, stakeholderDataArray, 'stakeholder');
        stakeholderCoords.forEach((c: any) => bounds.extend(c));
        hasData = true;
      }
    }

    return hasData;
  }

  private addPolygonLayer(geometry: any, id: string, contextType: string, isHighlight: boolean = false, color?: string, itemData?: any): void {
    const sourceId = this.UNIFIED_POLYGONS_SOURCE;
    const fillLayerId = this.UNIFIED_CONTEXT_POLYGON_FILL;
    const outlineLayerId = this.UNIFIED_CONTEXT_OUTLINE;

    const source = this.map.getSource(sourceId) as mapboxgl.GeoJSONSource;
    if (!source) return;

    let existingFeatures: any[] = [];
    try {
      const currentData = source.serialize ? (source as any).serialize() : null;
      if (currentData && currentData.data && currentData.data.features) {
        existingFeatures = currentData.data.features;
      } else {
        const sourceData = (source as any)._data;
        if (sourceData && sourceData.features) {
          existingFeatures = sourceData.features;
        }
      }
    } catch (e) {
      existingFeatures = [];
    }

    const fillColor = color || this.getContextTypeColor(contextType);

    const featureId = `polygon-${id}`;
    const newFeature = {
      type: 'Feature' as const,
      id: featureId,
      geometry,
      properties: { 
        contextType,
        itemId: id,
        fillColor,
        itemData: itemData ? JSON.stringify(itemData) : null
      }
    };

    const filteredFeatures = existingFeatures.filter((f: any) => f.id !== featureId);
    filteredFeatures.push(newFeature);

    source.setData({
      type: 'FeatureCollection',
      features: filteredFeatures
    });

    if (!this.map.getLayer(fillLayerId)) {
      this.map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': ['get', 'fillColor'],
          'fill-opacity': 0.25
        }
      });
    }

    if (!this.map.getLayer(outlineLayerId)) {
      this.map.addLayer({
        id: outlineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['get', 'fillColor'],
          'line-width': 2,
          'line-dasharray': [2, 1]
        }
      });
    }
  }

  private normalizeContextType(contextType: string): string {
    const normalized = contextType.toLowerCase();
    if (normalized === 'societal') {
      return 'social';
    }
    return normalized;
  }

  private getContextTypeColor(contextType: string): string {
    const colorMap: { [key: string]: string } = {
      'physical': '#ff8800',
      'regulatory': '#aa00ff',
      'political': '#0066ff',
      'social': '#ff0066',
      'societal': '#ff0066',
      'emotional': '#00cc66'
    };
    const normalized = this.normalizeContextType(contextType);
    return colorMap[normalized] || '#808080';
  }

  private addPointFeatures(coordinates: number[][], sourceId: string, iconName: string, isHighlight: boolean = false, itemData?: any, contextType?: string): void {
    const unifiedSourceId = this.UNIFIED_POINTS_SOURCE;
    const layerId = this.UNIFIED_CONTEXT_POINTS_LAYER;

    const source = this.map.getSource(unifiedSourceId) as mapboxgl.GeoJSONSource;
    if (!source) return;

    let existingFeatures: any[] = [];
    try {
      const currentData = source.serialize ? (source as any).serialize() : null;
      if (currentData && currentData.data && currentData.data.features) {
        existingFeatures = currentData.data.features;
      } else {
        const sourceData = (source as any)._data;
        if (sourceData && sourceData.features) {
          existingFeatures = sourceData.features;
        }
      }
    } catch (e) {
      existingFeatures = [];
    }

    const newFeatures = coordinates.map((c, index) => {
      let featureItemData = itemData;
      
      if (Array.isArray(itemData) && itemData.length > index) {
        featureItemData = itemData[index];
      }

      const featureId = `${sourceId}-${index}`;

      return {
        type: 'Feature' as const,
        id: featureId,
        geometry: { type: 'Point' as const, coordinates: c },
        properties: {
          itemData: featureItemData ? JSON.stringify(featureItemData) : null,
          contextType: contextType || '',
          iconName,
          itemId: itemData?.id || itemData?.uuid || sourceId,
          name: featureItemData?.name || featureItemData?.text || '',
          address: featureItemData?.address || ''
        }
      };
    });

    const featureIds = new Set(newFeatures.map(f => f.id));
    const filteredFeatures = existingFeatures.filter((f: any) => !featureIds.has(f.id));
    filteredFeatures.push(...newFeatures);

    source.setData({
      type: 'FeatureCollection',
      features: filteredFeatures
    });

    if (!this.map.getLayer(layerId)) {
      this.map.addLayer({
        id: layerId,
        type: 'symbol',
        source: unifiedSourceId,
        layout: {
          'icon-image': ['get', 'iconName'],
          'icon-size': 0.45,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      });
    }
  }

  private extendBounds(bounds: mapboxgl.LngLatBounds, geometry: any): void {
    const coords = geometry.coordinates;

    const traverse = (coordArray: any[]) => {
      coordArray.forEach((c: any) => {
        if (typeof c[0] === 'number' && typeof c[1] === 'number') {
          bounds.extend(c as [number, number]);
        } else if (Array.isArray(c)) {
          traverse(c);
        }
      });
    };

    traverse(coords);
  }

  private isPhysicalContextLayer(layer: any): boolean {
    if (layer.id.includes('physical-icon') || 
        layer.id.includes('physical-') ||
        (layer.source && typeof layer.source === 'string' && layer.source.includes('physical'))) {
      return true;
    }
    
    if (!layer.source || typeof layer.source !== 'string' || layer.source.trim() === '') {
      return false;
    }
    
    try {
      const source = this.map.getSource(layer.source);
      if (source && (source as any).data) {
        const data = (source as any).data;
        const features = data.features || (data.type === 'Feature' ? [data] : []);
        
        if (Array.isArray(features) && features.length > 0) {
          const feature = features[0];
          if (feature.properties) {
            if (feature.properties.contextType === 'physical') {
              return true;
            }
            
            const itemData = feature.properties.itemData;
            if (itemData) {
              try {
                const parsed = typeof itemData === 'string' ? JSON.parse(itemData) : itemData;
                if (parsed.contextType === 'physical' || parsed.type === 'physical') {
                  return true;
                }
              } catch (e) {
                console.error(e);
              }
            }
          }
        } else if (data.type === 'Feature') {
          if (data.properties) {
            if (data.properties.contextType === 'physical') {
              return true;
            }
            const itemData = data.properties.itemData;
            if (itemData) {
              try {
                const parsed = typeof itemData === 'string' ? JSON.parse(itemData) : itemData;
                if (parsed.contextType === 'physical' || parsed.type === 'physical') {
                  return true;
                }
              } catch (e) {
                console.error(e);
              }
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
    return false;
  }

  private restoreAllContextLayersOpacity(): void {
    if (this.map.getLayer(this.UNIFIED_CONTEXT_POLYGON_FILL)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POLYGON_FILL, 'fill-opacity', 0.25);
    }
    if (this.map.getLayer(this.UNIFIED_CONTEXT_OUTLINE)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-opacity', 1);
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_OUTLINE, 'line-width', 2);
    }
    if (this.map.getLayer(this.UNIFIED_CONTEXT_POINTS_LAYER)) {
      this.map.setPaintProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'icon-opacity', 1);
      this.map.setLayoutProperty(this.UNIFIED_CONTEXT_POINTS_LAYER, 'icon-size', 0.45);
    }

    const style = this.map.getStyle();
    if (!style) return;

    const contextLayerPrefixes = ['all-', 'context-polygon-'];

    style.layers?.forEach(layer => {
      const isContextLayer = contextLayerPrefixes.some(p => layer.id.includes(p));
      if (!isContextLayer) return;

      if (layer.type === 'fill') {
        this.map.setPaintProperty(layer.id, 'fill-opacity', 0.25);
      } else if (layer.type === 'line') {
        this.map.setPaintProperty(layer.id, 'line-opacity', 1);
        this.map.setPaintProperty(layer.id, 'line-width', 2);
      } else if (layer.type === 'symbol') {
        this.map.setPaintProperty(layer.id, 'icon-opacity', 1);
        this.map.setLayoutProperty(layer.id, 'icon-size', 0.45);
      }
    });
  }

  private clearAllContextLayers(): void {
    const polygonsSource = this.map.getSource(this.UNIFIED_POLYGONS_SOURCE) as mapboxgl.GeoJSONSource;
    if (polygonsSource) {
      polygonsSource.setData({ type: 'FeatureCollection', features: [] });
    }

    const pointsSource = this.map.getSource(this.UNIFIED_POINTS_SOURCE) as mapboxgl.GeoJSONSource;
    if (pointsSource) {
      pointsSource.setData({ type: 'FeatureCollection', features: [] });
    }

    const style = this.map.getStyle();
    if (!style) return;

    const prefixes = [
      'context-polygon-',
      'all-',
      'highlight-',
      'all-physical-',
      'single-stakeholder-'
    ];

    style.layers?.forEach(layer => {
      if (prefixes.some(p => layer.id.includes(p))) {
        if (this.map.getLayer(layer.id)) {
          this.map.removeLayer(layer.id);
        }
      }
    });

    Object.keys(style.sources).forEach(sourceId => {
      if (prefixes.some(p => sourceId.includes(p))) {
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId);
        }
      }
    });
  }

  private clearAllAnalysisLayers(): void {
    // Clear unified sources so context items disappear from the map
    const polygonsSource = this.map.getSource(this.UNIFIED_POLYGONS_SOURCE) as mapboxgl.GeoJSONSource;
    if (polygonsSource) {
      polygonsSource.setData({ type: 'FeatureCollection', features: [] });
    }

    const pointsSource = this.map.getSource(this.UNIFIED_POINTS_SOURCE) as mapboxgl.GeoJSONSource;
    if (pointsSource) {
      pointsSource.setData({ type: 'FeatureCollection', features: [] });
    }

    const style = this.map.getStyle();
    if (!style) return;

    const prefixes = [
      'context-polygon-',
      'all-',
      'highlight-',
      'all-physical-'
    ];

    style.layers?.forEach(layer => {
      let shouldRemove = false;
      
      if (prefixes.some(p => layer.id.includes(p))) {
        shouldRemove = true;
      }
      
      if (!shouldRemove && layer.type === 'symbol') {
        try {
          const iconImage = this.map.getLayoutProperty(layer.id, 'icon-image');
          if (iconImage === 'physical-icon' || iconImage === 'physical_num') {
            shouldRemove = true;
          }
        } catch (e) {
          console.error(e);
        }
      }
      
      if (shouldRemove && this.map.getLayer(layer.id)) {
        this.map.removeLayer(layer.id);
      }
    });

    Object.keys(style.sources).forEach(sourceId => {
      if (prefixes.some(p => sourceId.includes(p))) {
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId);
        }
      }
    });
    this.arePhysicalItemsVisible = false;
  }

  private ensurePolygonSelected(): void {
    if (this.draw && this.selectedAreaGeometry) {
      const allFeatures = this.draw.getAll();
      const polygonFeature = allFeatures.features.find((f: any) => f.geometry.type === 'Polygon');
      if (polygonFeature && polygonFeature.id) {
        (this.draw as any).changeMode('simple_select', {
          featureIds: [polygonFeature.id]
        });
      }
    }
  }

  private onContextPinClick(contextData: any, contextType: string): void {
    this.ngZone.run(() => {
      this.ensurePolygonSelected();
      const capitalizedType = this.capitalizeFirstLetter(contextType);

      if (this.sidebarCollapsed()) {
        this.sidebarCollapsed.set(false);
      }

      if (capitalizedType !== 'Physical') {
        const originalItem = this.findOriginalContextItem(contextData, capitalizedType);
        if (originalItem) {
          const current = this.contextRightSidebar.expandedItem();
          if (!(current && current.item === originalItem && current.type === capitalizedType)) {
            this.contextRightSidebar.expandedItem.set(null);
            this.selectedContextItem = { item: originalItem, type: capitalizedType };
            const chip = this.contextRightSidebar.contextChipsTypes.find(c => c.name === capitalizedType);
            if (chip) chip.expanded = true;
            this.contextRightSidebar.openIndividualContextDetail(originalItem, capitalizedType);
          }
          this.cdr.detectChanges();
          setTimeout(() => {
            this.contextRightSidebar.scrollToCurrentlyExpandedCard();
          }, 350);
          return;
        }
      }

      this.contextRightSidebar.expandedItem.set(null);
      this.selectedContextItem = { item: contextData, type: capitalizedType };
      const chip = this.contextRightSidebar.contextChipsTypes.find(c => c.name === capitalizedType);
      if (chip) {
        chip.expanded = true;
      }
      this.contextRightSidebar.openIndividualContextDetail(this.selectedContextItem.item, capitalizedType);
      this.cdr.detectChanges();
      setTimeout(() => {
        this.contextRightSidebar.scrollToCurrentlyExpandedCard();
      }, 350);
    });
  }

  private findOriginalContextItem(contextData: any, contextType: string): any {
    const normalizedType = this.normalizeContextType(contextType);

    for (const context of this.contextAnalysisResponse) {
      const ctxType = this.normalizeContextType(context.context_type || '');
      if (ctxType !== normalizedType) continue;

      if (Array.isArray(context.context_subtypes)) {
        for (const subtype of context.context_subtypes) {
          if (Array.isArray(subtype.context_items)) {
            const match = subtype.context_items.find((item: any) =>
              item.uuid && contextData.uuid && item.uuid === contextData.uuid
            );
            if (match) return subtype;
          }
        }
      }

      if (Array.isArray(context.context_items)) {
        const match = context.context_items.find((item: any) =>
          item.uuid && contextData.uuid && item.uuid === contextData.uuid
        );
        if (match) return match;
      }
    }
    return null;
  }

  private onStakeholderPinClick(stakeholderData: any): void {
    this.ngZone.run(() => {
      this.ensurePolygonSelected();
      if (this.sidebarCollapsed()) {
        this.sidebarCollapsed.set(false);
      }
      const stakeholderWithContext = this.findStakeholderWithContext(stakeholderData);
      
      if (stakeholderWithContext) {
        this.contextRightSidebar.expandedItem.set(null);
        this.selectedContextItem = { item: stakeholderWithContext, type: 'Stakeholder' };
        this.contextRightSidebar.openIndividualContextDetail(this.selectedContextItem.item, this.selectedContextItem.type);
        this.cdr.detectChanges();
        setTimeout(() => {
          this.contextRightSidebar.scrollToCurrentlyExpandedCard();
        }, 350);
      }
    });
  }

  private findStakeholderWithContext(stakeholder: any): any {
    const contexts: any[] = [];
    
    if (Array.isArray(this.physicalContextAnalysisResponse)) {
      this.physicalContextAnalysisResponse.forEach((physicalItem: any) => {
        const stakeholders = physicalItem?.stakeholders || [];
        const matchingStakeholder = stakeholders.find((st: any) => 
          st.name === stakeholder.name && isEqual(st.locations.points.coordinates[0], stakeholder.locations.points.coordinates[0])
        );
        
        if (matchingStakeholder) {
          const { stakeholders: _, ...contextData } = physicalItem;
          contexts.push(contextData);
        }
      });
    }

    return {
      ...stakeholder,
      contexts: contexts
    };
  }

  private showTooltip(lngLat: mapboxgl.LngLat | [number, number], title: string, subtitle?: string): void {
    if (this.tooltipHideTimeout) {
      clearTimeout(this.tooltipHideTimeout);
      this.tooltipHideTimeout = null;
    }

    this.hideTooltipImmediately();

    let content = `<div style="font-weight: 600; color: #333;">${title}</div>`;
    if (subtitle) {
      content += `<div style="font-size: 12px; color: #666; margin-top: 4px;">${subtitle}</div>`;
    }

    this.mapPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 25,
      anchor: 'bottom',
      className: 'map-tooltip'
    })
      .setLngLat(lngLat)
      .setHTML(content)
      .addTo(this.map);
  }

  private hideTooltip(): void {
    this.ngZone.runOutsideAngular(() => {
      if (this.tooltipHideTimeout) {
        clearTimeout(this.tooltipHideTimeout);
      }

      this.tooltipHideTimeout = setTimeout(() => {
        this.hideTooltipImmediately();
        this.tooltipHideTimeout = null;
      }, 50);
    });
  }

  private hideTooltipImmediately(): void {
    if (this.mapPopup) {
      this.mapPopup.remove();
      this.mapPopup = null;
    }
    this.hideAllMultiItemTooltips();
  }

  private capitalizeFirstLetter(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private loadSavedAnalysisPolygon(): void {
    this.loadingSavedAnalysis.set(true);
    this.updateTrashButtonState();
    this._contextService.getSavedContexts().subscribe({
      next: (response: any) => {
        if (response?.polygon && Array.isArray(response.polygon) && response.polygon.length > 0) {
          this.hasSavedAnalysis = true;
          
          const geoJsonPolygon = this.convertPolygonToGeoJSON(response.polygon);
          
          if (geoJsonPolygon) {
            this.showPolygonLimitExceededErrorPopup.set(false);
            this.selectedAreaGeometry = geoJsonPolygon;
            
            const feature = {
              type: 'Feature' as const,
              geometry: geoJsonPolygon,
              properties: {}
            };
            
            this.draw.add(feature);

            const payload = this.preparePayload(this.selectedAreaGeometry);
            this.getContextAiAnalysis(payload.payload, { polygon: payload.polygon });
            
            setTimeout(() => {
              this.selectExistingPolygon();
              this.updateDrawControls();
              this.focusOnAnalysisArea();
            }, 100);
          }
        } else {
          this.hasSavedAnalysis = false;
        }
      },
      error: (error) => {
        console.error('Error loading saved analysis polygon:', error);
        this.hasSavedAnalysis = false;
        this.applyMapComponentInitialView();
        this.setDrawButtonsVisibility(true);
        this.updateDrawControls();
        this.loadingSavedAnalysis.set(false);
        this.updateTrashButtonState();
      },
      complete: () => {
        this.setDrawButtonsVisibility(true);
        this.updateDrawControls();
        if (!this.hasSavedAnalysis) {
          this.applyMapComponentInitialView();
          this.loadingSavedAnalysis.set(false);
          this.updateTrashButtonState();
        }
      }
    });
  }

  private applyMapComponentInitialView(): void {
    const coord = this.globals?.projectConfig?.polygons?.coordinates?.[0]?.[0]?.[0];
    if (Array.isArray(coord) && coord.length >= 2) {
      this.map.setCenter([coord[0], coord[1]]);
      this.map.setZoom(10);
    }
  }

  private updateDrawControls(): void {
    if (!this.draw) return;
    if (this.isChangingMode) return;
    
    const getMode = (this.draw as any).getMode ? (this.draw as any).getMode.bind(this.draw) : () => this.lastDrawMode;
    const currentMode = getMode();

    if (this.selectedAreaGeometry) {
      const allFeatures = this.draw.getAll();
      const polygonFeature = allFeatures.features.find((f: any) => f.geometry.type === 'Polygon');
      if (polygonFeature && polygonFeature.id) {
        if (currentMode !== 'simple_select') {
          this.isChangingMode = true;
          (this.draw as any).changeMode('simple_select', {
            featureIds: [polygonFeature.id]
          });
          this.lastDrawMode = 'simple_select';
          setTimeout(() => this.isChangingMode = false, 0);
        }
      } else {
        if (currentMode !== 'simple_select') {
          this.isChangingMode = true;
          (this.draw as any).changeMode('simple_select');
          this.lastDrawMode = 'simple_select';
          setTimeout(() => this.isChangingMode = false, 0);
        }
      }
      
      this.disablePolygonButton(true);
    } else {
      this.disablePolygonButton(false);
      if (currentMode !== 'simple_select') {
        this.isChangingMode = true;
        (this.draw as any).changeMode('simple_select');
        this.lastDrawMode = 'simple_select';
        setTimeout(() => this.isChangingMode = false, 0);
      }
    }
  }

  private disablePolygonButton(disable: boolean): void {
    setTimeout(() => {
      const polygonButton = document.querySelector('.mapbox-gl-draw_polygon') as HTMLButtonElement;
      if (polygonButton) {
        polygonButton.disabled = disable;
        if (disable) {
          polygonButton.style.opacity = '0.5';
          polygonButton.style.cursor = 'not-allowed';
        } else {
          polygonButton.style.opacity = '1';
          polygonButton.style.cursor = 'pointer';
        }
      }
    }, 50);
  }

  private setDrawButtonsVisibility(show: boolean): void {
    const maxAttempts = 10;
    const attemptDelay = 30;
    let attempts = 0;

    const tryToggle = () => {
      const buttons = document.querySelectorAll('.mapbox-gl-draw_polygon, .mapbox-gl-draw_trash');
      if (buttons.length > 0 || attempts >= maxAttempts) {
        buttons.forEach(btn => {
          const el = btn as HTMLButtonElement;
          el.style.display = show ? '' : 'none';
        });
        this.updateTrashButtonState();
        return;
      }
      attempts += 1;
      setTimeout(tryToggle, attemptDelay);
    };

    tryToggle();
  }

  private updateTrashButtonState(): void {
    const disableTrash = this.isProcessing() || this.loadingSavedAnalysis();
    setTimeout(() => {
      const trashButton = document.querySelector('.mapbox-gl-draw_trash') as HTMLButtonElement;
      if (!trashButton) return;

      trashButton.disabled = disableTrash;
      trashButton.style.opacity = disableTrash ? '0.5' : '1';
      trashButton.style.cursor = disableTrash ? 'not-allowed' : 'pointer';
    }, 50);
  }

  private selectExistingPolygon(): void {
    if (!this.draw) return;
    if (this.isChangingMode) return;

    const getMode = (this.draw as any).getMode ? (this.draw as any).getMode.bind(this.draw) : () => this.lastDrawMode;
    const getSelectedIds = (this.draw as any).getSelectedIds ? (this.draw as any).getSelectedIds.bind(this.draw) : () => [];

    const allFeatures = this.draw.getAll();
    const polygonFeature = allFeatures.features.find((f: any) => f.geometry.type === 'Polygon');
    if (!polygonFeature || !polygonFeature.id) return;

    const currentMode = getMode();
    const selectedIds: Array<string | number> = getSelectedIds() || [];
    const polygonId = String(polygonFeature.id);
    const alreadySelected = selectedIds.some(id => String(id) === polygonId);

    if (currentMode !== 'simple_select' || !alreadySelected) {
      this.isChangingMode = true;
      (this.draw as any).changeMode('simple_select', {
        featureIds: [polygonId]
      });
      this.lastDrawMode = 'simple_select';
      setTimeout(() => this.isChangingMode = false, 0);
    }
  }

  private deleteSavedAnalysis(): void {
    this._contextService.deleteSavedContexts().subscribe({
      next: () => {
        this.hasSavedAnalysis = false;
      },
      error: (error) => {
        console.error('Error deleting saved analysis:', error);
        this.hasSavedAnalysis = false;
      }
    });
  }

  private convertPolygonToGeoJSON(polygon: Array<{lon: number, lat: number}> | number[][]): any {
    if (!polygon || polygon.length < 3) {
      return null;
    }

    let coordinates: number[][];
    
    if (polygon.length > 0 && typeof polygon[0] === 'object' && 'lon' in polygon[0]) {
      coordinates = (polygon as Array<{lon: number, lat: number}>).map(coord => [coord.lon, coord.lat]);
    } else {
      coordinates = polygon as number[][];
    }
    
    const firstCoord = coordinates[0];
    const lastCoord = coordinates[coordinates.length - 1];
    if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
      coordinates.push([firstCoord[0], firstCoord[1]]);
    }

    return {
      type: 'Polygon',
      coordinates: [coordinates]
    };
  }
}
